/**
 * ChangePlanModal Component
 * Confirmation before an in-app plan change (WP-BILLING-19 Part B, review L-4:
 * "Confirm with amount", decided 2026-09-27). One click on a plan card used to
 * change the subscription and could charge the card at once.
 */
import Button from '@/components/ui/Button';
import { describePlanChange, formatPlanPrice } from '@/utils/planChange';
import type { Plan, Subscription } from '@/types/billing';

export interface ChangePlanModalProps {
  isOpen: boolean;
  subscription: Subscription;
  plan: Plan | null;
  onClose: () => void;
  onConfirm: (plan: Plan) => Promise<void>;
  isLoading?: boolean;
}

export default function ChangePlanModal({
  isOpen,
  subscription,
  plan,
  onClose,
  onConfirm,
  isLoading = false,
}: ChangePlanModalProps) {
  if (!isOpen || !plan) return null;

  const handleClose = () => {
    if (!isLoading) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="change-plan-modal-title"
    >
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={handleClose} aria-hidden="true" />

      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6">
          <h2 id="change-plan-modal-title" className="text-lg font-semibold text-gray-900">
            Switch to {plan.name}?
          </h2>
          <dl className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-gray-500">Current plan</dt>
              <dd className="text-gray-900">
                {subscription.planName} ·{' '}
                {formatPlanPrice(subscription.planAmount, subscription.planCurrency, subscription.planInterval)}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">New plan</dt>
              <dd className="font-medium text-gray-900">
                {plan.name} · {formatPlanPrice(plan.amount, plan.currency, plan.interval)}
              </dd>
            </div>
          </dl>
          <p className="mt-4 text-sm text-gray-600">{describePlanChange(subscription, plan)}</p>

          <div className="mt-6 flex gap-3 justify-end">
            <Button variant="outline" onClick={handleClose} disabled={isLoading}>
              Keep Current Plan
            </Button>
            <Button onClick={() => onConfirm(plan)} isLoading={isLoading}>
              Switch Plan
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
